import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import i18n from 'i18next';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMatch, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {usePresentationThemeDetailQuery} from '../../../../hooks/usePresentationThemeDetailQuery';
import Button from '../../../../incase-domain/components/button/Button';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {updatePresentationThemeService} from '../../../../service/presentationTheme.service';
import {IUpdateError} from '../../../../store/actions/types/error.types';
import {defaultFonts} from '../../../../utils/defaultFonts';
import LoadingSpinnerComponent from '../../../components/LoadingSpinnerComponent';
import PresentationThemeFavoriteColors from '../components/PresentationThemeFavoriteColors';
import PresentationThemeFonts from '../components/PresentationThemeFonts';
import PresentationThemeFontSizes from '../components/PresentationThemeFontSizes';
import PresentationThemeTextElement from '../components/PresentationThemeTextElement';
import PresentationThemeQuestionElement from '../components/questionElement/PresentationThemeQuestionElement';
import InputWithCharacterCounter from '../../../../components/slides/highlighted/common/InputWithCharacterCounter';
import Label from '../../../../incase-domain/components/label/Label';

function EditPresentationTheme(): JSX.Element {
    const {t} = useTranslation();
    const client = useQueryClient();
    const navigate = useNavigate();
    const match = useMatch('dashboard/editTheme/:themeId');

    const initialFontSizes = useMemo(
        () => [
            {size: FONT_SIZE.EXTRA_SMALL, value: 0.5},
            {size: FONT_SIZE.SMALL, value: 1},
            {size: FONT_SIZE.MEDIUM, value: 1.5},
            {size: FONT_SIZE.LARGE, value: 2},
            {size: FONT_SIZE.EXTRA_LARGE, value: 2.5},
        ],
        []
    );

    const fetchTheme = usePresentationThemeDetailQuery(match?.params.themeId);

    const [presentationTheme, setPresentationTheme] = useState<IPresentationTheme>({
        _id: '',
        themeName: '',
        fontSizes: initialFontSizes,
        fonts: [],
        defaultFonts: [],
        lastModified: 0,
    });
    const [isMounted, setIsMounted] = useState(true);

    useEffect(() => {
        return () => {
            setIsMounted(false);
        };
    }, []);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            await fetchTheme.refetch();
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fetchTheme.data) {
            setPresentationTheme({
                _id: fetchTheme.data._id,
                themeName: fetchTheme.data.themeName,
                favoriteColors: fetchTheme.data.favoriteColors,
                fontSizes: fetchTheme.data.fontSizes || initialFontSizes,
                elementStyles: fetchTheme.data.elementStyles,
                fonts: fetchTheme.data.fonts,
                defaultFonts: fetchTheme.data.defaultFonts ? fetchTheme.data.defaultFonts : defaultFonts,
                lastModified: fetchTheme.data.lastModified,
            });
        }
    }, [fetchTheme.data, initialFontSizes]);

    const updatePresentationTheme: UseMutationResult<
        boolean,
        IUpdateError,
        {
            themeId: string;
            theme: IPresentationTheme;
        }
    > = useMutation(
        (variables: {themeId: string; theme: IPresentationTheme}) => updatePresentationThemeService(variables.themeId, variables.theme),
        {
            onSuccess: () => {
                Promise.all([
                    client.invalidateQueries({queryKey: QUERY_KEYS.theme.detail(match?.params.themeId || '')}),
                    client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()}),
                ]).then(() => {
                    navigate('/dashboard');
                    toast.success(i18n.t('presentationThemesPage.toasts.presThemeEditSuccess'));
                });
            },
        }
    );

    if (fetchTheme.isLoading) return <LoadingSpinnerComponent />;

    return (
        <>
            <div className="u-flex u-h-between u-v-center l-grid l-grid--noGaps ">
                <Label size="lg">{t('presentationThemesPage.themeName')}:</Label>
                <div className="l-col:4">
                    <InputWithCharacterCounter
                        className="c-sidepanel__input"
                        placeholder={t('presentationThemesPage.themeName')}
                        maxLength={15}
                        value={presentationTheme.themeName}
                        onChange={(e) => {
                            setPresentationTheme({...presentationTheme, themeName: e});
                        }}
                    />
                </div>
            </div>
            <PresentationThemeFavoriteColors
                favoriteColorsList={presentationTheme?.favoriteColors}
                setPresentationTheme={setPresentationTheme}
                presentationTheme={presentationTheme}
            />
            <PresentationThemeFontSizes setPresentationTheme={setPresentationTheme} presentationTheme={presentationTheme} />
            <PresentationThemeFonts setPresentationTheme={setPresentationTheme} presentationTheme={presentationTheme} />
            <hr />
            <PresentationThemeTextElement presentationTheme={presentationTheme} setPresentationTheme={setPresentationTheme} />
            <hr />
            <PresentationThemeQuestionElement presentationTheme={presentationTheme} setPresentationTheme={setPresentationTheme} />
            <div className="c-group u-pt-2" style={{justifyContent: 'right'}}>
                <Button
                    size="sm"
                    variant="soft"
                    color="secondary"
                    wide
                    onClick={() => {
                        navigate('/dashboard');
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    size="sm"
                    variant="solid"
                    color="secondary"
                    wide
                    disabled={!presentationTheme.themeName || presentationTheme.themeName.trim().length === 0}
                    onClick={() => {
                        if (isMounted) {
                            updatePresentationTheme.mutate({
                                themeId: match?.params.themeId || '',
                                theme: presentationTheme,
                            });
                        }
                    }}
                >
                    {t('common.save')}
                </Button>
            </div>
        </>
    );
}

export default EditPresentationTheme;
